import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, Divider,
    IconButton, CircularProgress, MenuItem, InputAdornment,
    FormControlLabel, Switch, Menu, ListItemIcon, ListItemText, Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PixIcon from '@mui/icons-material/Pix';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryIcon from '@mui/icons-material/History';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import api from '../services/api';
import { isVisualizador } from '../services/auth';
import { toastSuccess, toastError, alertValidacao, confirmAcao } from '../services/alerts';

const initialForm = {
    banco: '', agencia: '', numeroConta: '', chavePix: '',
    saldoInicial: '', ativo: true, tipoConta: 'CORRENTE', idPsicologo: ''
};

export default function ContaBancariaForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const soLeitura = isVisualizador();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!!id);
    const [psicologos, setPsicologos] = useState([]);
    const [formData, setFormData] = useState(initialForm);
    const [saldoAtual, setSaldoAtual] = useState(null);
    const [menuAnchor, setMenuAnchor] = useState(null);

    const carregarConta = () => {
        if (!id) return;
        return api.get(`/contas-bancarias/${id}`)
            .then(r => {
                const d = r.data;
                setFormData({
                    banco: d.banco || '',
                    agencia: d.agencia || '',
                    numeroConta: d.numeroConta || '',
                    chavePix: d.chavePix || '',
                    saldoInicial: d.saldoInicial ?? '',
                    ativo: d.ativo ?? true,
                    tipoConta: d.tipoConta || 'CORRENTE',
                    idPsicologo: d.idPsicologo || ''
                });
                setSaldoAtual(d.saldoAtual);
            })
            .catch(() => {})
            .finally(() => setFetching(false));
    };

    useEffect(() => {
        api.get('/psicologos').then(r => { const d = r.data; setPsicologos(Array.isArray(d) ? d : (d?.content ?? [])); }).catch(() => {});
        carregarConta();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const set = (field) => (e) => setFormData(prev => ({ ...prev, [field]: e.target.value }));

    const handleSave = async () => {
        if (!formData.banco.trim()) { alertValidacao('O nome do banco é obrigatório.'); return; }
        if (!formData.agencia.trim()) { alertValidacao('A agência é obrigatória.'); return; }
        if (!formData.numeroConta.trim()) { alertValidacao('O número da conta é obrigatório.'); return; }

        setLoading(true);
        try {
            const payload = {
                ...formData,
                saldoInicial: formData.saldoInicial !== '' ? parseFloat(formData.saldoInicial) : 0,
                idPsicologo: formData.idPsicologo || null,
            };
            if (id) {
                await api.put(`/contas-bancarias/${id}`, payload);
            } else {
                await api.post('/contas-bancarias', payload);
            }
            toastSuccess(id ? 'Conta atualizada com sucesso' : 'Conta criada com sucesso');
            setTimeout(() => navigate('/conta-bancaria'), 800);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fecharMenu = () => setMenuAnchor(null);

    const handleAlterarStatus = async () => {
        fecharMenu();
        const inativando = formData.ativo;
        const ok = await confirmAcao({
            title: inativando ? 'Inativar conta' : 'Reativar conta',
            text: inativando
                ? 'A conta será inativada, mas o histórico financeiro será mantido. Continuar?'
                : 'Reativar esta conta bancária?',
            confirmButtonText: inativando ? 'Inativar' : 'Reativar',
            icon: inativando ? 'warning' : 'question',
            danger: inativando,
        });
        if (!ok) return;
        try {
            await api.patch(`/contas-bancarias/${id}/${inativando ? 'inativar' : 'reativar'}`);
            toastSuccess(inativando ? 'Conta inativada' : 'Conta reativada');
            carregarConta();
        } catch (e) {
            console.error(e);
        }
    };

    const handleCopiarPix = async () => {
        fecharMenu();
        if (!formData.chavePix) { toastError('Conta sem chave PIX.'); return; }
        try {
            await navigator.clipboard.writeText(formData.chavePix);
            toastSuccess('Chave PIX copiada');
        } catch {
            toastError('Não foi possível copiar.');
        }
    };

    const handleHistorico = () => {
        fecharMenu();
        navigate('/financeiro/historico?tab=2');
    };

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                <IconButton onClick={() => navigate('/conta-bancaria')} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#333', flexGrow: 1 }}>
                    {soLeitura ? 'Visualizar Conta Bancária' : id ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
                </Typography>
                {id && (
                    <>
                        <Chip
                            label={formData.ativo ? 'Ativa' : 'Inativa'}
                            color={formData.ativo ? 'success' : 'default'}
                            size="small"
                        />
                        <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ bgcolor: '#fff', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                            <MoreVertIcon />
                        </IconButton>
                        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={fecharMenu}>
                            {!soLeitura && (
                                <MenuItem onClick={handleAlterarStatus}>
                                    <ListItemIcon>
                                        {formData.ativo ? <BlockIcon fontSize="small" color="warning" /> : <CheckCircleIcon fontSize="small" color="success" />}
                                    </ListItemIcon>
                                    <ListItemText>{formData.ativo ? 'Inativar conta' : 'Reativar conta'}</ListItemText>
                                </MenuItem>
                            )}
                            <MenuItem onClick={handleCopiarPix} disabled={!formData.chavePix}>
                                <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Copiar chave PIX</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleHistorico}>
                                <ListItemIcon><HistoryIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Histórico de alterações</ListItemText>
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>

                {id && saldoAtual != null && (
                    <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Saldo atual</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: Number(saldoAtual) >= 0 ? 'success.main' : 'error.main' }}>
                            R$ {Number(saldoAtual).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </Typography>
                    </Box>
                )}

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Dados da Conta
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField sx={{ flex: 2, minWidth: 200 }} label="Banco *" value={formData.banco} onChange={set('banco')} placeholder="Ex: Nubank, Itaú, Bradesco" disabled={soLeitura} />
                        <TextField sx={{ flex: 1, minWidth: 120 }} label="Agência *" value={formData.agencia} onChange={set('agencia')} disabled={soLeitura} />
                        <TextField sx={{ flex: 1, minWidth: 120 }} label="Conta *" value={formData.numeroConta} onChange={set('numeroConta')} disabled={soLeitura} />
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            sx={{ flex: 1, minWidth: 160 }}
                            label="Chave PIX"
                            value={formData.chavePix}
                            onChange={set('chavePix')}
                            InputProps={{ startAdornment: <InputAdornment position="start"><PixIcon fontSize="small" color="action" /></InputAdornment> }}
                            disabled={soLeitura}
                        />
                        <TextField select sx={{ flex: 1, minWidth: 160 }} label="Tipo de conta" value={formData.tipoConta} onChange={set('tipoConta')} disabled={soLeitura}>
                            <MenuItem value="CORRENTE">Corrente</MenuItem>
                            <MenuItem value="POUPANCA">Poupança</MenuItem>
                            <MenuItem value="PAGAMENTO">Pagamento</MenuItem>
                        </TextField>
                    </Box>
                </Box>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
                    Saldo e Psicólogo
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                        sx={{ flex: 1, minWidth: 160 }}
                        label="Saldo inicial (R$)"
                        type="number"
                        value={formData.saldoInicial}
                        onChange={set('saldoInicial')}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                        helperText={id ? 'Alteração não recalcula o saldo atual' : 'Saldo de abertura da conta'}
                        disabled={soLeitura}
                    />
                    <TextField select sx={{ flex: 1, minWidth: 160 }} label="Psicólogo" value={formData.idPsicologo} onChange={set('idPsicologo')} disabled={soLeitura}>
                        <MenuItem value=""><em>Nenhum</em></MenuItem>
                        {psicologos.map(p => (
                            <MenuItem key={p.id_psicologo} value={p.id_psicologo}>{p.nomeCompleto}</MenuItem>
                        ))}
                    </TextField>
                </Box>

                <Divider sx={{ my: 4 }} />

                <FormControlLabel
                    control={
                        <Switch
                            checked={formData.ativo}
                            onChange={(e) => setFormData(prev => ({ ...prev, ativo: e.target.checked }))}
                            color="success"
                            disabled={soLeitura}
                        />
                    }
                    label="Conta ativa"
                />

                <Divider sx={{ my: 4 }} />

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button variant="outlined" onClick={() => navigate('/conta-bancaria')} sx={{ color: '#666', borderColor: '#ccc' }}>
                        {soLeitura ? 'Voltar' : 'Cancelar'}
                    </Button>
                    {!soLeitura && (
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            sx={{ px: 5 }}
                        >
                            {id ? 'Salvar Alterações' : 'Criar Conta'}
                        </Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
}
